import { useState, useEffect, type MouseEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { FiFileText, FiBarChart2, FiUsers, FiDollarSign, FiSettings, FiUser, FiLogOut } from 'react-icons/fi';
import { formatCurrency } from '../utils/formatNumber';
import type { Counterparty, CounterpartyTeam } from '../types';
import { clearAuthToken } from '../api/api';
import { formatNextSalaryHoldLine } from '../utils/salaryHoldDisplay';

const MENU_BASE = [
  { name: 'Заявки', path: '/requests', icon: FiFileText },
  { name: 'Дашборд', path: '/dashboard', icon: FiBarChart2, responsibleOnly: true },
  { name: 'Команда', path: '/team', icon: FiUsers },
  { name: 'Транзакции', path: '/transactions', icon: FiDollarSign },
  { name: 'Настройки', path: '/settings', icon: FiSettings },
];

export type SidebarBalanceSummary = {
  total: number;
  available: number;
  held: number;
  nextReleaseAt: string | null;
  isTeam: boolean;
};

interface Props {
  isOpen: boolean;
  onToggle: () => void;
  user: Counterparty | null;
  team: CounterpartyTeam | null;
  balanceSummary: SidebarBalanceSummary | null;
  isResponsible?: boolean;
}

export default function CounterpartySidebar({ isOpen, onToggle, user, team, balanceSummary, isResponsible = false }: Props) {
  const location = useLocation();
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showPagination, setShowPagination] = useState(false);

  const menu = MENU_BASE.filter((item) => {
    if ((item as { responsibleOnly?: boolean }).responsibleOnly) return isResponsible;
    if (item.path === '/team') return isResponsible;
    return true;
  });
  useEffect(() => {
    const h = (e: Event) => {
      const d = (e as CustomEvent<{ currentPage: number; totalPages: number; showPagination: boolean }>).detail;
      if (d) {
        setCurrentPage(d.currentPage);
        setTotalPages(d.totalPages);
        setShowPagination(d.showPagination);
      }
    };
    window.addEventListener('paginationUpdate', h);
    return () => window.removeEventListener('paginationUpdate', h);
  }, []);

  const handlePageChange = (page: number) => {
    window.dispatchEvent(new CustomEvent('pageChange', { detail: { page } }));
  };

  const handleLogout = () => {
    clearAuthToken();
    window.location.href = '/login';
  };

  const handleCopyBalance = async (e: MouseEvent<HTMLElement>) => {
    e.preventDefault();
    e.stopPropagation();
    const total = balanceSummary?.total ?? 0;
    const balanceNumber = total.toLocaleString('ru-RU', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    try {
      await navigator.clipboard.writeText(balanceNumber);
      window.dispatchEvent(new CustomEvent('showToast', { detail: { message: 'Баланс скопирован', type: 'success' } }));
    } catch {
      window.dispatchEvent(new CustomEvent('showToast', { detail: { message: 'Ошибка копирования', type: 'error' } }));
    }
  };

  const holdReleaseSubtitle = balanceSummary
    ? formatNextSalaryHoldLine(balanceSummary.nextReleaseAt, balanceSummary.held)
    : null;

  return (
    <>
      {isOpen && (
        <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={onToggle} />
      )}
      <aside
        className={`fixed top-0 left-0 h-dvh bg-gradient-to-b from-blue-900 to-indigo-900 dark:from-blue-950 dark:to-indigo-950 text-white z-50 transition-transform lg:translate-x-0 lg:static w-[250px] max-w-[88vw] flex flex-col ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="px-4 pt-4 pb-[14px] border-b border-white/10">
          <h1 className="text-lg font-bold">ЛК Контрагента</h1>
          <div className="flex items-center gap-2 mt-0.5 min-w-0">
            <svg className="w-4 h-4 shrink-0 text-white/80" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
              <circle cx="9" cy="7" r="4" />
              <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
              <path d="M16 3.13a4 4 0 0 1 0 7.75" />
            </svg>
            <p className="text-sm font-medium text-white/90 truncate">{isResponsible && team ? team.name : (user?.name || 'Контрагент')}</p>
          </div>
        </div>
        <nav className="flex-1 p-4 relative">
          <div className="space-y-2 relative">
            {menu.map(({ name, path, icon: Icon }) => {
              const active = location.pathname === path;
              return (
                <Link
                  key={path}
                  to={path}
                  onClick={() => onToggle()}
                  className={`relative flex items-center gap-3 px-4 h-12 min-h-12 rounded-xl transition-all duration-200 z-10 ${
                    active ? 'text-white bg-white/20 shadow-lg' : 'text-white/70 hover:bg-white/10 hover:text-white'
                  }`}
                >
                  <Icon className="w-5 h-5 shrink-0" />
                  <span className="truncate">{name}</span>
                </Link>
              );
            })}
          </div>
        </nav>
        <div className="border-t border-white/10 pb-2">
          {showPagination && totalPages > 1 && location.pathname === '/requests' && (
            <div className="p-4 pb-3 border-b border-white/10">
              <div className="flex items-center justify-between space-x-2">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="px-3 py-1 text-xs border border-white/30 rounded-md hover:bg-white/10 disabled:opacity-50 text-white/70"
                >
                  ←
                </button>
                <span className="text-xs text-white/70 px-2">{currentPage} / {totalPages}</span>
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 text-xs border border-white/30 rounded-md hover:bg-white/10 disabled:opacity-50 text-white/70"
                >
                  →
                </button>
              </div>
            </div>
          )}
          <div className="p-4 pt-3">
          <button
            onClick={() => navigate('/settings')}
            className="w-full flex items-center gap-3 mb-4 rounded-lg hover:bg-white/5 transition-colors"
          >
            <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
              <FiUser className="w-5 h-5" />
            </div>
            <div className="min-w-0 text-left">
              <p className="font-medium truncate">{user?.name || 'Контрагент'}</p>
            </div>
          </button>
          <button
              type="button"
              onClick={() => navigate('/transactions')}
              className="w-full mb-3 px-3 py-2 bg-white/10 rounded-lg hover:bg-white/15 transition-colors text-left space-y-1"
            >
              <p className="text-xs text-white/70">{balanceSummary?.isTeam ? 'Баланс команды' : 'Мой баланс'}</p>
              <p
                className="font-semibold text-yellow-300 cursor-copy inline-block hover:text-yellow-200 transition-colors"
                title="Нажмите, чтобы скопировать общую сумму (доступно + в холде)"
                onClick={handleCopyBalance}
              >
                Всего {formatCurrency(balanceSummary?.total ?? 0)}
              </p>
              {balanceSummary && (
                <>
                  <p className="text-xs text-white/85">
                    Доступно: <span className="font-medium text-emerald-200">{formatCurrency(balanceSummary.available)}</span>
                  </p>
                  <p className="text-xs text-white/85">
                    В холде: <span className="font-medium text-amber-200/95">{formatCurrency(balanceSummary.held)}</span>
                  </p>
                  {holdReleaseSubtitle && (
                    <p className="text-[11px] leading-snug text-sky-200/95">{holdReleaseSubtitle}</p>
                  )}
                </>
              )}
            </button>
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-2 px-4 py-2 text-white/80 hover:bg-white/10 rounded-lg transition-colors"
          >
            <FiLogOut className="w-4 h-4" />
            <span>Выйти</span>
          </button>
          </div>
        </div>
      </aside>
    </>
  );
}
